import { useState } from 'react';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import TeamAnalysis from './TeamAnalysis';
import MatchupAnalysis from './MatchupAnalysis';
import BookmakerAnalysis from './BookmakerAnalysis';
import LockedFeatureCard from '../upgrade/LockedFeatureCard';
import UpgradeModal from '../upgrade/UpgradeModal';

const SPORTS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' },
  { key: 'boxing_boxing', label: 'Boxing' },
];

const TABS = [
  { key: 'team', label: 'Team', minTier: 'pro' },
  { key: 'matchup', label: 'Matchup', minTier: 'premium' },
  { key: 'bookmaker', label: 'Bookmaker', minTier: 'premium' },
];

const TIER_ORDER = { free: 0, pro: 1, premium: 2 };

const TAB_DESCRIPTIONS = {
  team: 'Analyze team ATS records, over/under trends, and moneyline performance from completed games.',
  matchup: 'Explore head-to-head ATS and over/under trends between any two teams.',
  bookmaker: 'Compare bookmaker accuracy and closing line value across sports and seasons.',
};

export default function PatternFinder() {
  const [tab, setTab] = useState('team');
  const [sport, setSport] = useState('americanfootball_nfl');
  const tier = useEffectiveTier();
  const [upgradeModal, setUpgradeModal] = useState({ open: false, tier: 'pro' });

  function isTabLocked(t) {
    return TIER_ORDER[tier] < TIER_ORDER[t.minTier];
  }

  function handleTabClick(t) {
    if (isTabLocked(t)) {
      setUpgradeModal({ open: true, tier: t.minTier });
    } else {
      setTab(t.key);
    }
  }

  const activeTab = TABS.find((t) => t.key === tab);
  const currentTabLocked = activeTab && isTabLocked(activeTab);

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Historical Patterns</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Pattern Finder</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Analyze ATS records, over/under trends, and bookmaker performance from completed games.</p>

      {/* Sport + Analysis selectors */}
      <div className="flex flex-wrap items-end gap-4 mb-5 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
        <div className="flex items-end gap-0">
          <span className="text-[11px] uppercase tracking-[1px] font-semibold text-[#9CA3AF] dark:text-[#505a6e] mr-3 pb-2 select-none" style={{ fontFamily: 'var(--font-sans)' }}>Sport</span>
          {SPORTS.map((s) => (
            <button key={s.key} onClick={() => setSport(s.key)}
              className={`px-0 mr-5 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
                sport === s.key
                  ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                  : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
              }`}
              style={{ fontFamily: 'var(--font-sans)' }}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="w-px h-5 bg-[#E5E7EB] dark:bg-[#1e2536] mb-2 flex-shrink-0" />

        <div className="flex items-end gap-0">
          <span className="text-[11px] uppercase tracking-[1px] font-semibold text-[#9CA3AF] dark:text-[#505a6e] mr-3 pb-2 select-none" style={{ fontFamily: 'var(--font-sans)' }}>Analysis</span>
          {TABS.map((t) => {
            const locked = isTabLocked(t);
            const selected = tab === t.key;
            return (
              <button key={t.key} onClick={() => handleTabClick(t)}
                className={`px-0 mr-5 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 flex items-center gap-1.5 ${
                  locked
                    ? 'font-medium text-[#9CA3AF] dark:text-[#505a6e] border-transparent cursor-pointer'
                    : selected
                    ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                    : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
                }`}
                style={{ fontFamily: 'var(--font-sans)' }}>
                {t.label}
                {locked && (
                  <span className="text-[9px] font-bold px-1 py-px rounded bg-[#E8F0FE] dark:bg-[rgba(0,91,179,0.15)] text-[#005BB3] dark:text-[#3b82f6] uppercase">
                    {t.minTier === 'premium' ? 'Premium' : 'Pro'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active section */}
      {currentTabLocked ? (
        <LockedFeatureCard
          featureName={`Pattern Finder — ${activeTab.label} Analysis`}
          description={TAB_DESCRIPTIONS[tab]}
          requiredTier={activeTab.minTier}
        />
      ) : (
        <>
          {tab === 'team' && <TeamAnalysis sport={sport} />}
          {tab === 'matchup' && <MatchupAnalysis sport={sport} />}
          {tab === 'bookmaker' && <BookmakerAnalysis sport={sport} />}
        </>
      )}

      <UpgradeModal
        open={upgradeModal.open}
        onClose={() => setUpgradeModal({ ...upgradeModal, open: false })}
        suggestedTier={upgradeModal.tier}
        triggerFeature="Pattern Finder"
      />
    </div>
  );
}
